import React, { useEffect, useState, useRef } from 'react'
import DataTable from 'react-data-table-component'
import Select from 'react-select'
import { FiMenu } from 'react-icons/fi'
import { getAllData as getAllListCollege } from '../../lib/collegeList2'
import { getSelectionsForStudent, addSelections, deleteSelectionById } from '../../lib/studentlistcollege'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { stateOptions, stateSelectStyles } from '../../Data/CollegeManagement/StudentCollegeListData'
import {
	student911CollegeColumnTitles,
	student911DefaultVisibleColumnTitles,
	getStudent911CollegeColumns
} from '../../Data/CollegeManagement/Student911CollegeListData'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'

const COLLEGE_LIST2_TABLE_STYLES = {
	headRow: {
		style: {
			alignItems: 'stretch',
			minHeight: 'unset',
			width: '100%'
		}
	},
	headCells: {
		style: {
			fontSize: '11px',
			whiteSpace: 'normal',
			overflow: 'visible',
			textOverflow: 'clip',
			wordBreak: 'break-word',
			lineHeight: 1.25,
			minHeight: '48px',
			paddingTop: '10px',
			paddingBottom: '10px',
			paddingLeft: '8px',
			paddingRight: '8px',
			alignItems: 'center'
		}
	}
}

const CollegeList911 = ({ studentId }) => {
	const [colleges, setColleges] = useState([])
	const [originalSelections, setOriginalSelections] = useState([])
	const [selections, setSelections] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [activeTab, setActiveTab] = useState('global')
	const [selectedStates, setSelectedStates] = useState([])
	const [openColumnMenu, setOpenColumnMenu] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	const { user } = useAuth()
	const effectiveStudentId = studentId || user?.id
	const columnMenuRef = useRef(null)

	const [visibleColumns, setVisibleColumns] = useState(() => {
		const visible = new Set(student911DefaultVisibleColumnTitles)
		return student911CollegeColumnTitles.map((title) => visible.has(title))
	})

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const loadData = async () => {
		if (!effectiveStudentId) return
		try {
			setLoading(true)
			setError('')
			const [list, sel] = await Promise.all([getAllListCollege(), getSelectionsForStudent(effectiveStudentId)])
			setColleges(list || [])
			setSelections(sel || [])
			setOriginalSelections(sel || [])
		} catch (err) {
			console.error(err)
			setError('Failed to load college data')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		loadData()
	}, [effectiveStudentId])

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (columnMenuRef.current && !columnMenuRef.current.contains(event.target) && !event.target.classList.contains('column_menu')) {
				setOpenColumnMenu(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handleUpdate = async () => {
		try {
			setLoading(true)
			const originalCollegeIds = originalSelections.map((r) => Number(r.collegeId))
			const currentCollegeIds = selections.map((r) => Number(r.collegeId))

			const collegeIdsToAdd = currentCollegeIds.filter((id) => !originalCollegeIds.includes(id))
			const recordsToDelete = originalSelections.filter((r) => !currentCollegeIds.includes(Number(r.collegeId)))

			if (collegeIdsToAdd.length > 0) {
				await addSelections(
					collegeIdsToAdd.map((collegeId) => ({
						studentId: effectiveStudentId,
						collegeId
					}))
				)
			}

			if (recordsToDelete.length > 0) {
				await Promise.all(recordsToDelete.map((r) => deleteSelectionById(r.id)))
			}

			showToastMessage('College list updated successfully!')
			await loadData()
		} catch (err) {
			console.error(err)
			setError('Failed to update college list')
		} finally {
			setLoading(false)
		}
	}

	const q = searchQuery.toLowerCase()
	const filteredColleges = colleges.filter((college) => {
		const stateMatch = selectedStates.length === 0 || selectedStates.includes(college.state)
		if (!q) return stateMatch
		const fields = [
			college.id,
			college.class,
			college.college_name,
			college.state,
			college.city,
			college.ranking,
			college.gpa,
			college.acceptance,
			college['0_10'],
			college['1_11'],
			college['26_50'],
			college['51_75'],
			college['76_100'],
			college.low_sat,
			college.high_sat,
			college.low_act,
			college.high_act,
			college.tuition,
			college.undergraduate_enrollment
		]
		const searchMatch = fields.some((f) => f !== null && f !== undefined && String(f).toLowerCase().includes(q))
		return stateMatch && searchMatch
	})

	const myColleges = filteredColleges.filter((c) => selections.some((s) => Number(s.collegeId) === Number(c.id)))
	const currentData = activeTab === 'my' ? myColleges : filteredColleges

	const columns = getStudent911CollegeColumns(selections, setSelections, effectiveStudentId)
	const visibleColumnsData = columns.filter((_, index) => visibleColumns[index])

	return (
		<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="card-header border-bottom d-flex justify-content-between align-items-center">
				<div className="d-flex align-items-center">
					<button
						type="button"
						className="btn btn-link p-0 me-2"
						data-bs-toggle="collapse"
						data-bs-target="#collapseCollegeList911"
						aria-expanded="false"
						aria-controls="collapseCollegeList911"
					>
						<i className="fa fa-chevron-down"></i>
					</button>
					<h5 className="mb-0 d-inline-block">College List</h5>
				</div>
			</div>
			<div className="collapse" id="collapseCollegeList911">
				<div className="card-body">
					{error && (
						<div className="alert alert-danger alert-dismissible fade show" role="alert">
							<strong>Error:</strong> {error}
							<button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></button>
						</div>
					)}

					<ul className="nav nav-tabs mb-3">
						<li className="nav-item">
							<button className={`nav-link ${activeTab === 'global' ? 'active' : ''}`} onClick={() => setActiveTab('global')} type="button">
								All College
							</button>
						</li>
						<li className="nav-item">
							<button className={`nav-link ${activeTab === 'my' ? 'active' : ''}`} onClick={() => setActiveTab('my')} type="button">
								My Colleges
							</button>
						</li>
					</ul>

					<div className="mb-3">
						<div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
							<div className="d-flex align-items-center gap-3 flex-wrap">
								<div className="position-relative">
									<FiMenu
										className="column_menu"
										onClick={() => setOpenColumnMenu(!openColumnMenu)}
										style={{ cursor: 'pointer', fontSize: '20px' }}
									/>
									{openColumnMenu && (
										<div
											className="column-toggle-checkboxes position-absolute border rounded shadow p-3 bg-white"
											ref={columnMenuRef}
											style={{
												top: '100%',
												left: 0,
												zIndex: 1000,
												minWidth: '200px',
												backgroundColor: '#fff',
												boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
											}}
										>
											{student911CollegeColumnTitles.map((title, index) => (
												<div key={title} className="form-check">
													<input
														className="checkbox_animated"
														type="checkbox"
														id={`col911-${index}`}
														checked={visibleColumns[index]}
														onChange={() => {
															const next = [...visibleColumns]
															next[index] = !next[index]
															setVisibleColumns(next)
														}}
													/>
													<label className="form-check-label" htmlFor={`col911-${index}`}>
														{title}
													</label>
												</div>
											))}
										</div>
									)}
								</div>
								<div style={{ width: '300px', minWidth: '200px' }}>
									<Select
										isMulti
										options={stateOptions}
										className="state-select"
										placeholder="Select states..."
										onChange={(selectedOptions) => {
											const selectedValues = selectedOptions ? selectedOptions.map((option) => option.value) : []
											setSelectedStates(selectedValues)
										}}
										styles={stateSelectStyles}
									/>
								</div>
							</div>
							<input
								type="text"
								className="form-control"
								placeholder="Search..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								style={{ width: '300px', minWidth: '200px' }}
							/>
						</div>
					</div>

					{loading && !colleges.length ? (
						<div className="text-center">
							<div className="spinner-border" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
							<p className="mt-2">Loading college data...</p>
						</div>
					) : (
						<>
							<div className="table-responsive rdt-head-wrap w-100 college-list-911">
								<DataTable
									columns={visibleColumnsData}
									data={currentData}
									striped={true}
									center={false}
									pagination
									paginationPerPage={10}
									paginationRowsPerPageOptions={[10, 25, 50, 100]}
									highlightOnHover={true}
									responsive={true}
									noDataComponent="No colleges found"
									customStyles={{
										...COLLEGE_LIST2_TABLE_STYLES,
										table: {
											style: {
												width: '100%'
											}
										},
										tableWrapper: {
											style: {
												width: '100%'
											}
										},
										responsiveWrapper: {
											style: {
												width: '100%'
											}
										},
										rows: {
											style: {
												color: '#000'
											}
										},
										cells: {
											style: {
												fontSize: '12px',
												padding: '8px',
												color: '#000'
											}
										}
									}}
								/>
							</div>
							<div className="d-flex justify-content-end mt-3">
								<button type="button" onClick={handleUpdate} className="btn btn-primary" disabled={loading}>
									<i className="fa fa-save me-2"></i>
									{loading ? 'Updating...' : 'UPDATE'}
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	)
}

export { CollegeList911 }
